import type { AttendanceStatus, EvaluationStatus } from '@/api/generated';
import messages from '@/components/AttendanceTable/messages';
import type {
    AttendanceRecord,
    AttendanceTableItem,
    BasicCustomer,
} from '@/components/AttendanceTable/types';
import { isAttendanceMarkingEnabled } from '@/utils';

import type { MessageDescriptor } from 'react-intl';

const getAttendanceStatusMessage = (status: AttendanceStatus): MessageDescriptor => {
    switch (status) {
        case 'ATTENDED':
            return messages.attendedStatus;
        case 'NO_SHOW':
            return messages.noShowStatus;
        case 'PARTIALLY_ATTENDED':
            return messages.partiallyAttendedStatus;
        case 'NOT_MARKED':
        case 'REMOVED':
        default:
            return messages.unknown;
    }
};

const getEvaluationStatusMessage = (status: EvaluationStatus): MessageDescriptor => {
    switch (status) {
        case 'COMPLETED':
            return messages.evaluationCompletedStatus;
        case 'INCOMPLETE':
            return messages.evaluationIncompleteStatus;
        default:
            return messages.unknown;
    }
};

export const convertAttendanceRecordsToTableItems = ({
    records,
    customers,
}: {
    records: AttendanceRecord[];
    customers: BasicCustomer[];
}): AttendanceTableItem[] => {
    return records.map(({ learningActivityAttendanceStatus, learner }) => {
        const userSFDCAccountIds =
            learner.organizations?.nodes.map(({ sfdcAccountId }) => sfdcAccountId) ?? [];
        const customer = customers.find(
            ({ sfdc_account_id }) =>
                sfdc_account_id && userSFDCAccountIds.includes(sfdc_account_id),
        );
        const subscription: MessageDescriptor = (
            learner.licenses?.nodes.filter(
                ({ productModality }) => productModality === 'DIGITAL',
            ) ?? []
        ).length
            ? messages.hasSubscription
            : messages.doesNotHaveSubscription;
        return {
            fullName: `${learner.firstName} ${learner.lastName}`.trim() || '--',
            email: learner.emailAddress,
            customer: customer?.customer_name ?? '--',
            subscription: subscription,
            attendanceStatus: getAttendanceStatusMessage(learningActivityAttendanceStatus),
            evaluationStatus: getEvaluationStatusMessage(learner.evaluationStatus),
        };
    });
};

export const supportsAttendance = (lms_type?: string | null) => {
    return isAttendanceMarkingEnabled(lms_type);
};
